import ThemedElement from "shared/components/ThemedElement";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Autocomplete,
  Grid,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ExpandMore, LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import MDBox from "shared/components/MDBox";
import useFormUser from "../../hooks/useFormUser";
import { getRole } from "modules/rol/services/rolService";
import { isConcessionRole } from "../../model/userConcession";
import * as userServices from "modules/usuario/services/userService";

// Función para validar contraseña fuerte
const validateStrongPassword = (password) => {
  const errors = [];

  if (password.length < 6) {
    errors.push("Mínimo 6 caracteres");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Al menos una mayúscula");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Al menos un número");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("Al menos un carácter especial (!@#$%^&*)");
  }

  return errors;
};

const FormUser = ({
  onSubmit,
  onCancel,
  onBusyChange,
  afterSubmit = () => {},
  initialData = {},
  isEditMode = false,
  loading: externalLoading = false,
  userId = null,
}) => {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [errorRoles, setErrorRoles] = useState(null);
  const [concessions, setConcessions] = useState([]);
  const [loadingConcessions, setLoadingConcessions] = useState(false);
  const [errorConcessions, setErrorConcessions] = useState(null);
  const [concessionRetry, setConcessionRetry] = useState(0);
  const formik = useFormUser(onSubmit, afterSubmit, initialData, isEditMode, {
    roles,
    concessions,
    loadingConcessions,
    errorConcessions,
  });
  const needsConcession = isConcessionRole(
    roles.find((role) => String(role.id) === String(formik.values.roleId))
  );
  const selectedConcession =
    concessions.find((row) => row.code === formik.values.concessionCode) || null;
  const { validateForm } = formik;
  useEffect(() => {
    validateForm();
  }, [roles, concessions, loadingConcessions, errorConcessions, validateForm]);
  useEffect(() => {
    if (!needsConcession) return;
    const controller = new AbortController();
    let active = true;
    setLoadingConcessions(true);
    setErrorConcessions(null);
    setConcessions([]);
    userServices
      .getUserConcessionsService({ signal: controller.signal })
      .then((data) => {
        if (active) setConcessions(data);
      })
      .catch((error) => {
        if (active)
          setErrorConcessions(
            error.response?.data?.message ||
              error.message ||
              "No se pudieron cargar las concesiones."
          );
      })
      .finally(() => {
        if (active) setLoadingConcessions(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [needsConcession, concessionRetry]);
  // Estados para cambio de contraseña
  const [passwordExpanded, setPasswordExpanded] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [changingPassword, setChangingPassword] = useState(false);

  // Estados para mostrar/ocultar contraseña
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados para Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    let isMounted = true;

    const loadRoles = async () => {
      try {
        const rolesData = await getRole();
        if (isMounted) {
          // Asegurar que rolesData sea un array
          const rolesArray = Array.isArray(rolesData) ? rolesData : [];
          setRoles(rolesArray);
        }
      } catch (error) {
        if (isMounted) {
          setErrorRoles("Error al cargar roles");
          console.error("Error loading roles:", error);
        }
      } finally {
        if (isMounted) {
          setLoadingRoles(false);
        }
      }
    };

    loadRoles();

    return () => {
      isMounted = false;
    };
  }, []);

  // Función para validar la contraseña en tiempo real
  const validatePassword = (password) => {
    const errors = validateStrongPassword(password);
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  // Función para manejar el cambio de contraseña
  const handleChangePassword = async () => {
    // Validar contraseña fuerte
    if (!validatePassword(newPassword)) {
      return;
    }

    // Validar que las contraseñas coincidan
    if (newPassword !== confirmPassword) {
      setPasswordErrors(["Las contraseñas no coinciden"]);
      return;
    }

    if (!userId) {
      setPasswordErrors(["ID de usuario no disponible"]);
      return;
    }

    setChangingPassword(true);

    try {
      // Verificamos si existe la función
      if (!userServices.changePasswordService) {
        throw new Error("Función de cambio de contraseña no disponible");
      }

      await userServices.changePasswordService(userId, newPassword);

      // Mostrar Snackbar de éxito
      showSnackbar("✅ Contraseña actualizada correctamente", "success");

      // Limpiar campos
      setNewPassword("");
      setConfirmPassword("");
      setPasswordErrors([]);
      setPasswordExpanded(false);
    } catch (error) {
      console.error("Error al cambiar contraseña:", error);

      // Mostrar Snackbar de error
      showSnackbar(error.message || "Error al cambiar la contraseña", "error");

      setPasswordErrors([error.message || "Error al cambiar la contraseña"]);
    } finally {
      setChangingPassword(false);
    }
  };

  // Función para obtener el texto de ayuda de la contraseña
  const getPasswordHelperText = () => {
    if (passwordErrors.length > 0) {
      return (
        <ThemedElement component="ul" style={{ margin: 0, paddingLeft: 20, color: "#d32f2f" }}>
          {passwordErrors.map((error, index) => (
            <li key={index} style={{ fontSize: "0.75rem" }}>
              {error}
            </li>
          ))}
        </ThemedElement>
      );
    }
    return "Mínimo 6 caracteres, 1 mayúscula, 1 número y 1 carácter especial";
  };

  const sectionSx = {
    p: { xs: 2, sm: 2.5 },
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    bgcolor: "background.paper",
  };
  // Mayor contraste dentro del modal: campos con borde más marcado y
  // etiquetas/textos más oscuros (los tokens se adaptan al modo oscuro).
  const fieldContrastSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 48,
      fontSize: ".875rem",
      "& .MuiOutlinedInput-input": { color: "text.primary" },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.35),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.6),
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: (theme) => theme.palette.info.main,
      },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.18),
      },
    },
    "& .MuiInputLabel-root:not(.Mui-error)": {
      fontSize: ".875rem",
      color: "text.primary",
      fontWeight: 500,
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: (theme) => theme.palette.text.disabled,
    },
    "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
      color: (theme) => theme.palette.info.main,
    },
    "& .MuiFormHelperText-root:not(.Mui-error)": {
      color: "text.secondary",
    },
  };
  // Determinar si el formulario está deshabilitado
  const isFormDisabled = formik.isSubmitting || externalLoading || changingPassword;

  useEffect(() => {
    onBusyChange?.(isFormDisabled);
  }, [isFormDisabled, onBusyChange]);

  return (
    <>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            overflowY: "auto",
            flex: 1,
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            ...fieldContrastSx,
          }}
        >
          {formik.status && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formik.status}
            </Alert>
          )}
          <Box sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Datos del usuario
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="username"
                  label="Nombre de Usuario"
                  value={formik.values.username || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                  disabled={isFormDisabled}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="Correo Electrónico"
                  value={formik.values.email || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={isFormDisabled}
                />
              </Grid>

              {!isEditMode && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="password"
                    label="Contraseña"
                    type="password"
                    value={formik.values.password || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    disabled={isFormDisabled}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Box sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Rol y estado
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={isEditMode ? 8 : 12}>
                <Autocomplete
                  options={roles}
                  value={
                    roles.find((role) => String(role.id) === String(formik.values.roleId)) || null
                  }
                  getOptionLabel={(role) => role.name || `Rol ${role.id}`}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  onChange={(_, role) =>
                    formik.setValues((values) => ({
                      ...values,
                      roleId: role ? Number(role.id) : "",
                      concessionCode: "",
                    }))
                  }
                  onBlur={() => formik.setFieldTouched("roleId", true)}
                  disabled={loadingRoles || isFormDisabled || Boolean(errorRoles)}
                  loading={loadingRoles}
                  noOptionsText="No hay roles disponibles"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={loadingRoles ? "Cargando roles..." : "Rol"}
                      error={Boolean(errorRoles || (formik.touched.roleId && formik.errors.roleId))}
                      helperText={errorRoles || (formik.touched.roleId && formik.errors.roleId)}
                    />
                  )}
                />
              </Grid>

              {needsConcession && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={concessions}
                    value={selectedConcession}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    onChange={(_, concession) =>
                      formik.setFieldValue("concessionCode", concession?.code || "")
                    }
                    onBlur={() => formik.setFieldTouched("concessionCode", true)}
                    disabled={isFormDisabled || loadingConcessions || Boolean(errorConcessions)}
                    loading={loadingConcessions}
                    noOptionsText="No hay concesiones de tipo PEAJE disponibles"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="concessionCode"
                        required
                        label={loadingConcessions ? "Cargando concesiones..." : "Concesión"}
                        error={Boolean(
                          errorConcessions ||
                            (formik.touched.concessionCode && formik.errors.concessionCode)
                        )}
                        helperText={
                          errorConcessions ||
                          (formik.touched.concessionCode && formik.errors.concessionCode) ||
                          (selectedConcession
                            ? `Código: ${selectedConcession.code}`
                            : "Seleccione la concesión a la que tendrá acceso el usuario.")
                        }
                      />
                    )}
                  />
                  {errorConcessions && (
                    <Button
                      onClick={() => setConcessionRetry((value) => value + 1)}
                      disabled={isFormDisabled}
                      sx={{ mt: 1 }}
                    >
                      Reintentar concesiones
                    </Button>
                  )}
                </Grid>
              )}
              {isEditMode && (
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: "action.hover",
                      minHeight: 48,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        m: 0,
                        gap: 1,
                        "& .MuiFormControlLabel-label": { fontSize: ".875rem", fontWeight: 600 },
                      }}
                      control={
                        <Switch
                          name="status"
                          checked={formik.values.status === 1}
                          onChange={(e) => formik.setFieldValue("status", e.target.checked ? 1 : 0)}
                          color="info"
                          disabled={isFormDisabled}
                        />
                      }
                      label={formik.values.status === 1 ? "Activo" : "Inactivo"}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          <Grid container>
            {/* Sección de cambio de contraseña (solo en modo edición) */}
            {isEditMode && (
              <Grid item xs={12}>
                <Accordion
                  expanded={passwordExpanded}
                  onChange={() => !isFormDisabled && setPasswordExpanded(!passwordExpanded)}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    m: "0 !important",
                    bgcolor: "background.paper",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      px: { xs: 2, sm: 2.5 },
                      minHeight: "64px !important",
                      "& .MuiAccordionSummary-content": { my: "16px !important" },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                      opacity: changingPassword ? 0.5 : 1,
                      pointerEvents: changingPassword ? "none" : "auto",
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      <LockReset color={changingPassword ? "disabled" : "info"} sx={{ mr: 1 }} />
                      <Typography
                        color={changingPassword ? "text.disabled" : "info"}
                        variant="subtitle2"
                        fontWeight="bold"
                      >
                        {changingPassword ? "Cambiando contraseña..." : "Cambiar Contraseña"}
                      </Typography>
                    </MDBox>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5, pt: 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, fontSize: ".8125rem" }}
                    >
                      La contraseña se actualiza por separado con el botón de esta sección.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showNewPassword ? "text" : "password"}
                          label="Nueva Contraseña"
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            validatePassword(e.target.value);
                          }}
                          disabled={isFormDisabled}
                          error={passwordErrors.length > 0}
                          autoComplete="new-password"
                          FormHelperTextProps={{
                            component: "div",
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={
                                    showNewPassword
                                      ? "Ocultar nueva contraseña"
                                      : "Mostrar nueva contraseña"
                                  }
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  edge="end"
                                  size="small"
                                  disabled={isFormDisabled}
                                >
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showConfirmPassword ? "text" : "password"}
                          label="Confirmar Contraseña"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (newPassword && e.target.value !== newPassword) {
                              setPasswordErrors(["Las contraseñas no coinciden"]);
                            } else {
                              validatePassword(newPassword);
                            }
                          }}
                          disabled={isFormDisabled}
                          error={passwordErrors.length > 0 && newPassword !== confirmPassword}
                          autoComplete="new-password"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={
                                    showConfirmPassword
                                      ? "Ocultar confirmación"
                                      : "Mostrar confirmación"
                                  }
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                  size="small"
                                  disabled={isFormDisabled}
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          component="div"
                          variant="caption"
                          color={passwordErrors.length ? "error" : "text.secondary"}
                        >
                          {getPasswordHelperText()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          color="info"
                          onClick={handleChangePassword}
                          disabled={
                            isFormDisabled ||
                            !newPassword ||
                            !confirmPassword ||
                            passwordErrors.length > 0 ||
                            newPassword !== confirmPassword
                          }
                          startIcon={changingPassword ? <CircularProgress size={20} /> : null}
                          sx={{
                            "&&": {
                              borderRadius: 1.5,
                              minHeight: 40,
                              textTransform: "none",
                              color: "#1a73e8",
                              borderColor: "#1a73e8",
                            },
                            "&&.Mui-disabled": {
                              color: "#748094",
                              borderColor: "divider",
                              opacity: 1,
                            },
                          }}
                        >
                          {changingPassword ? "Cambiando..." : "Actualizar Contraseña"}
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            boxShadow: "0 -4px 16px rgba(0,0,0,.03)",
            "& .MuiButton-root": {
              minHeight: 42,
              px: 2.5,
              textTransform: "none",
              fontSize: ".875rem",
              fontWeight: 700,
            },
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={isFormDisabled}
            sx={{
              "&&": { color: "text.primary", borderColor: "divider", bgcolor: "background.paper" },
              "&&:hover": {
                color: "text.primary",
                bgcolor: "action.hover",
                borderColor: "text.secondary",
              },
              "&&.Mui-disabled": { color: "text.disabled", borderColor: "divider", opacity: 1 },
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="info"
            sx={{
              "&&": {
                textTransform: "none",
                backgroundImage: "none",
                bgcolor: "#1a73e8",
                color: "#fff",
              },
              "&&:hover": { bgcolor: "#155fc0", color: "#fff" },
              "&&.Mui-disabled": { bgcolor: "#e4e9f0", color: "#697586", opacity: 1 },
            }}
            disabled={
              !formik.isValid ||
              (isEditMode && !formik.dirty) ||
              isFormDisabled ||
              loadingRoles ||
              Boolean(errorRoles) ||
              !formik.values.roleId ||
              (needsConcession &&
                (loadingConcessions || Boolean(errorConcessions) || !selectedConcession))
            }
          >
            {isFormDisabled ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              "Guardar cambios"
            ) : (
              "Crear usuario"
            )}
          </Button>
        </Box>
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: 3,
            fontWeight: 500,
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

FormUser.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onBusyChange: PropTypes.func,
  afterSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    username: PropTypes.string,
    email: PropTypes.string,
    password: PropTypes.string,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.number,
  }),
  isEditMode: PropTypes.bool,
  loading: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

FormUser.defaultProps = {
  afterSubmit: () => {},
  initialData: {},
  isEditMode: false,
  loading: false,
  userId: null,
};

export default FormUser;
